//Libs
import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GetQuickFilterTextParams,
  GridReadyEvent,
  ICellRendererParams,
} from "ag-grid-community";

//Local
import * as strings from "../../strings";
import { convertToReadableTimestamp } from "../../assets/js/convertToReadableTimestamp";
import { DocType, TableData } from "../../models/quickbook";
import { ExistingStiltInvoice } from "../../models/invoice";
import QuickbookDocTypeCustomFilter from "../../components/tables/QuickbookDocTypeCustomFilter";
import { ExistingPriceBookItem } from "../../models/price-book-item";
import { ExistingCustomer } from "../../models/customer";
import isEqual from "lodash/isEqual";
import SearchBox from "../../components/SearchBox";
import QuickbooksActionDropdown from "../../components/QuickbooksActionDropdown";
import AccountingSyncStatusCustomFilter from "../../components/tables/AccountingSyncStatusCustomFilter";
import { getReadableAccountingSyncStatus } from "../../models/accounting-sync";
import { DbWrite, hitAcctSync, removeFromSync } from "../../database";
import { createToastMessageID, phoneUtils } from "../../utils";
import { useToastMessageStore } from "../../store/toast-messages";
import { QBOCustomer } from "node-quickbooks";
import { StyledTooltip } from "../../components/StyledTooltip";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import LoadingSpinner from "../../components/LoadingSpinner";
import HeadingOne from "../../components/HeadingOne";

interface Props {
  siteKey: string;
  tableData: TableData[];
  quickbooksCustomers: Record<string, QBOCustomer[]>;
  paymentsInvoices: ExistingStiltInvoice[];
  waitingOnCustomerList: ExistingCustomer[];
  waitingOnPbItemList: ExistingPriceBookItem[];
  openInvoiceDialog: (invoiceID: string) => void;
  goToCustomerPage: (customerID: string) => void;
  onEditPBItem: (priceBookItem: ExistingPriceBookItem) => void;
  onCompareInvoice: (invoice: ExistingStiltInvoice) => void;
  isFetchingQuickbooksCustomers: boolean;
  children: {
    showDataWontSyncButton: React.ReactNode;
    connectionButtons: React.ReactNode;
  };
}

export default function QuickbooksPage({
  tableData,
  quickbooksCustomers,
  paymentsInvoices,
  waitingOnCustomerList,
  waitingOnPbItemList,
  openInvoiceDialog,
  goToCustomerPage,
  onEditPBItem,
  isFetchingQuickbooksCustomers,
  onCompareInvoice,
  ...props
}: Props) {
  const [gridReady, setGridReady] = useState(false);
  const gridRef = useRef<AgGridReact>(null);

  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [actionsLoading, setActionsLoading] = useState<boolean>(false);
  const addMessage = useToastMessageStore((state) => state.addToastMessage);

  const onGridReady = useCallback(() => {
    setGridReady(true);
  }, []);

  useLayoutEffect(() => {
    if (gridRef.current && gridReady) {
      gridRef.current.api?.sizeColumnsToFit();
    }
  }, [gridReady]);

  function rowSelectionChanged() {
    setSelectedRows(getSelectedRowData());
  }

  function getSelectedRowData() {
    const selectedData = gridRef.current?.api?.getSelectedRows();
    if (!selectedData) return [];
    return selectedData;
  }

  const onFilterTextBoxChanged = useCallback(() => {
    if (gridReady) {
      gridRef.current?.api.setGridOption(
        "quickFilterText",
        (document.getElementById("filter-text-box") as HTMLInputElement).value,
      );
    }
  }, [gridReady]);

  const actionDropdownButton = (
    <QuickbooksActionDropdown
      actionsLoading={actionsLoading}
      quickbooksActionSelected={async (
        actionType: "retrySync" | "removeFromSync",
        refPaths: any[],
      ) => {
        try {
          if (actionsLoading) {
            return;
          }
          setActionsLoading(true);
          const siteKeyID = refPaths[0]["refPath"].split("/")[1];

          const refPathList: string[] = [];
          for (const row of refPaths) {
            refPathList.push(row["refPath"]);
          }

          let result: string[] | undefined;
          if (actionType === "retrySync") {
            result = await hitAcctSync({ siteKeyID, refPathList });
            if (result && result.length > 0) {
              // Display error stating how many records failed to have a sync job created for them
              addMessage({
                id: createToastMessageID(),
                message: `Failed to retry sync for ${result.length} of ${refPathList.length} records. Please try again later.`,
                type: "error",
                dialog: false,
              });
            } else {
              // Show success message
              addMessage({
                id: createToastMessageID(),
                message: `Successfully retried sync for ${refPathList.length} records.`,
                type: "success",
                dialog: false,
              });
            }
          } else if (actionType === "removeFromSync") {
            result = await removeFromSync({ siteKeyID, refPathList });
            if (result && result.length > 0) {
              // Display error stating how many records failed to have a sync job created for them
              addMessage({
                id: createToastMessageID(),
                message: `Failed to remove ${result.length} of ${refPathList.length} records from syncing to Quickbooks. Please try again later.`,
                type: "error",
                dialog: false,
              });
            } else {
              // Show success message
              addMessage({
                id: createToastMessageID(),
                message: `Successfully removed ${refPathList.length} records from syncing to Quickbooks.`,
                type: "success",
                dialog: false,
              });
            }
          }

          setActionsLoading(false);
        } catch (error) {
          addMessage({
            id: createToastMessageID(),
            message: strings.ERROR_ATTEMPTING_ACCOUNTING_SYNC,
            type: "error",
            dialog: false,
          });
          setActionsLoading(false);
        }
      }}
      // invoiceActionSelected={props.invoiceActionSelected}
      selectedRows={selectedRows}
    />
  );

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        <span>
          <HeadingOne>{strings.QUICKBOOKS_SYNC_ERRORS}</HeadingOne>
          <h3 className="flex items-center gap-2">
            {`${tableData?.length ?? 0} Items Awaiting Sync`}
            {isFetchingQuickbooksCustomers && (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            )}
          </h3>
        </span>
        <span className="mb-6 ml-auto sm:mb-0 md:mt-1">
          {props.children.connectionButtons}
        </span>
      </div>
      <div className="flex flex-col-reverse flex-wrap gap-4 pt-4 xs:flex-row xs:items-center">
        {actionDropdownButton}
        <SearchBox onInput={onFilterTextBoxChanged} widthClasses="max-w-sm" />
        {props.children.showDataWontSyncButton}
      </div>
      <div className={"ag-theme-alpine flex h-[600px] flex-col"}>
        <QuickbooksListTable
          siteKey={props.siteKey}
          tableData={tableData}
          quickbooksCustomers={quickbooksCustomers}
          paymentsInvoices={paymentsInvoices}
          openInvoiceDialog={openInvoiceDialog}
          goToCustomerPage={goToCustomerPage}
          onEditPBItem={onEditPBItem}
          gridRef={gridRef}
          onGridReady={onGridReady}
          waitingOnCustomerList={waitingOnCustomerList}
          waitingOnPbItemList={waitingOnPbItemList}
          rowSelectionChanged={rowSelectionChanged}
          onCompareInvoice={onCompareInvoice}
        />
      </div>
    </div>
  );
}

const headerStyles =
  "px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase";

const commonColProps = {
  // minWidth: 150,
  flex: 1,
  resizable: true,
  filter: true,
  sortable: true,
  // wrapText: true,
  autoHeight: true,
  headerClass: headerStyles,
  wrapHeaderText: true,
  autoHeaderHeight: true,
};

interface QuickbooksListTableProps {
  siteKey: string;
  tableData: Props["tableData"];
  quickbooksCustomers: Props["quickbooksCustomers"];
  paymentsInvoices: Props["paymentsInvoices"];
  waitingOnCustomerList: Props["waitingOnCustomerList"];
  waitingOnPbItemList: Props["waitingOnPbItemList"];
  openInvoiceDialog: Props["openInvoiceDialog"];
  goToCustomerPage: Props["goToCustomerPage"];
  onEditPBItem: Props["onEditPBItem"];
  rowSelectionChanged: (event: GridReadyEvent) => void;
  gridRef: React.MutableRefObject<any>;
  onGridReady: (event: GridReadyEvent) => void;
  onCompareInvoice: Props["onCompareInvoice"];
}

/* TABLE COMPONENT */
const QuickbooksListTable = memo(
  ({
    siteKey,
    tableData,
    paymentsInvoices,
    waitingOnCustomerList,
    waitingOnPbItemList,
    openInvoiceDialog,
    goToCustomerPage,
    onEditPBItem,
    rowSelectionChanged,
    gridRef,
    onGridReady,
    quickbooksCustomers,
    onCompareInvoice,
  }: QuickbooksListTableProps) => {
    const [isConfirmingCustomerMatch, setIsConfirmingCustomerMatch] = useState<
      string | null
    >(null);
    const [isRestoringCustomer, setIsRestoringCustomer] = useState<
      string | null
    >(null);
    const addMessage = useToastMessageStore((state) => state.addToastMessage);

    const handleConfirmSameQBOCustomer = async (
      stiltCustomerId: string,
      qboCustomerId: string,
    ) => {
      try {
        setIsConfirmingCustomerMatch(stiltCustomerId);
        await DbWrite.customers.confirmSameQBOCustomer({
          uid: stiltCustomerId,
          siteKeyID: siteKey,
          customerID: stiltCustomerId,
          qboCustomerID: qboCustomerId,
        });
        setIsConfirmingCustomerMatch(null);
        addMessage({
          id: createToastMessageID(),
          message: "Successfully confirmed customer match",
          type: "success",
          dialog: false,
        });
      } catch (error) {
        console.error("Error confirming customer match:", error);
        addMessage({
          id: createToastMessageID(),
          message: strings.ERROR_ATTEMPTING_ACCOUNTING_SYNC,
          type: "error",
          dialog: false,
        });
        setIsConfirmingCustomerMatch(null);
      }
    };

    const restoreCustomerInQBO = async (
      stiltCustomerId: string,
      accountingSyncDocRefPath: string,
    ) => {
      try {
        await DbWrite.customers.restoreDeletedCustomerInQB({
          uid: stiltCustomerId,
          siteKeyID: siteKey,
          customerID: stiltCustomerId,
        });
        // Then after we restore the customer we need to retry syncing the underlying document
        const result = await hitAcctSync({
          siteKeyID: siteKey,
          refPathList: [accountingSyncDocRefPath],
        });
        if (result && result.length > 0) {
          // Display error stating how many records failed to have a sync job created for them
          addMessage({
            id: createToastMessageID(),
            message: `Failed to restore the deleted customer and sync this record. Please try again later.`,
            type: "error",
            dialog: false,
          });
        } else {
          // Show success message
          addMessage({
            id: createToastMessageID(),
            message: `Successfully restored the deleted customer and attempted to push this record to Quickbooks. Please wait a few minutes for the sync to complete.`,
            type: "success",
            dialog: false,
          });
        }
        setIsRestoringCustomer(null);
      } catch (error) {
        console.error(
          "Error restoring customer and syncing this record with Quickbooks:",
          error,
        );
        addMessage({
          id: createToastMessageID(),
          message: strings.ERROR_ATTEMPTING_ACCOUNTING_SYNC,
          type: "error",
          dialog: false,
        });
        setIsRestoringCustomer(null);
      }
    };

    const renderName = (params: ICellRendererParams): string => {
      const docType: DocType = params.data.docType;
      switch (docType) {
        case DocType.CUSTOMERS:
          return params.data.name;
        case DocType.PRICEBOOKITEMS:
          return params.data.title;
        case DocType.INVOICES:
          return `Invoice ${params.data.invoiceNumber}`;
        case DocType.PAYMENTS:
          const invoice = paymentsInvoices.find(
            (invoice) => invoice.id === params.data.invoiceID,
          );
          if (invoice) return `Payment for Invoice ${invoice.invoiceNumber}`;
          break;
        default:
          const _exhaustivenessCheck: never = docType;
          return _exhaustivenessCheck;
      }
      return "";
    };

    const renderType = (params: ICellRendererParams): JSX.Element => {
      return <div className="capitalize">{params.data.docType}</div>;
    };

    const renderSyncStatus = (params: ICellRendererParams): string =>
      getReadableAccountingSyncStatus(params.data.accountingSync.syncStatus);

    const renderMessage = (params: ICellRendererParams): JSX.Element => {
      // If there are existing QBO Customers and this is a new customer trying
      // to sync, it probably is a duplicate-name situation
      const existingQboCustomers = quickbooksCustomers[params.data.id];
      if (
        existingQboCustomers &&
        existingQboCustomers.length > 0 &&
        params.data.docType === DocType.CUSTOMERS
      ) {
        return (
          <div className="pointer-events-auto text-wrap py-2 leading-snug">
            <div>
              {existingQboCustomers.length} Quickbooks Customer(s) found with a
              matching name:
            </div>
            <div className="mt-2">
              {existingQboCustomers.map((customer) => (
                <div key={customer.Id} className="mb-1 flex items-center gap-2">
                  <div className="flex flex-col">
                    <hr className="w-full" />
                    <span className="font-medium">{customer.DisplayName}</span>
                    {customer.PrimaryPhone?.FreeFormNumber && (
                      <span className="text-sm text-gray-600">
                        {phoneUtils.display(
                          customer.PrimaryPhone.FreeFormNumber,
                        )}
                      </span>
                    )}
                    {customer.PrimaryEmailAddr?.Address && (
                      <span className="text-sm text-gray-600">
                        {customer.PrimaryEmailAddr.Address}
                      </span>
                    )}
                    <StyledTooltip
                      title={strings.CONFIRM_SAME_CUSTOMER_TOOLTIP}
                    >
                      <button
                        disabled={isConfirmingCustomerMatch === params.data.id}
                        onClick={() => {
                          handleConfirmSameQBOCustomer(
                            params.data.id,
                            customer.Id,
                          );
                        }}
                        className="max-w-fit cursor-pointer rounded-full border border-green-600 px-2 py-1 text-green-600 hover:bg-green-700 hover:text-white"
                      >
                        <div className="flex items-center gap-2">
                          {isConfirmingCustomerMatch === params.data.id
                            ? "Confirming..."
                            : "Confirm Same Customer"}
                          {isConfirmingCustomerMatch === params.data.id && (
                            <LoadingSpinner />
                          )}
                          {isConfirmingCustomerMatch !== params.data.id && (
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                      </button>
                    </StyledTooltip>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      // If the status message mentions a deleted customer, show a button to restore that customer in QBO
      if (
        params.data.accountingSync.statusMessage &&
        (params.data.accountingSync.statusMessage.includes(
          "customer you have specified has been deleted",
        ) ||
          params.data.accountingSync.statusMessage.includes(
            "Customer assigned to this transaction has been deleted",
          ) ||
          params.data.accountingSync.statusMessage.includes(
            "You cannot modify a list element that has been deleted",
          ))
      ) {
        return (
          <div className="text-wrap py-2 leading-snug">
            <div className="flex flex-col">
              {params.data.accountingSync.statusMessage}
              <StyledTooltip title={strings.RESTORE_CUSTOMER_IN_QB}>
                <button
                  disabled={isRestoringCustomer === params.data.id}
                  className="max-w-fit cursor-pointer rounded-full border border-green-600 px-2 py-1 text-green-600 hover:bg-green-700 hover:text-white"
                  onClick={() => {
                    setIsRestoringCustomer(params.data.id);
                    // An invoice or payment
                    if (params.data.customerID) {
                      restoreCustomerInQBO(
                        params.data.customerID,
                        params.data.refPath,
                      );
                    } else {
                      // A customer
                      restoreCustomerInQBO(params.data.id, params.data.refPath);
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    {isRestoringCustomer === params.data.id
                      ? "Restoring..."
                      : "Restore Customer"}
                    {isRestoringCustomer === params.data.id && (
                      <LoadingSpinner />
                    )}
                    {isRestoringCustomer !== params.data.id && (
                      <CheckCircleIcon className="h-5 w-5 text-green-600" />
                    )}
                  </div>
                </button>
              </StyledTooltip>
            </div>
          </div>
        );
      }

      // If the status message mentions an invoice, show a button to compare that invoice
      if (
        params.data.docType === DocType.INVOICES &&
        params.data.accountingSync.statusMessage &&
        params.data.accountingSync.statusMessage ===
          strings.DUPLICATE_INVOICE_NUM
      ) {
        return (
          <div className="text-wrap py-2 leading-snug">
            <div className="flex flex-col">
              {params.data.accountingSync.statusMessage}
              <StyledTooltip title="Compare Stilt Invoice to Quickbooks Invoice">
                <button
                  className="max-w-fit cursor-pointer rounded-full border border-green-600 px-2 py-1 text-green-600 hover:bg-green-700 hover:text-white"
                  onClick={() => onCompareInvoice(params.data)}
                >
                  Compare Invoice
                </button>
              </StyledTooltip>
            </div>
          </div>
        );
      }

      // Just display the normal message
      return (
        <div className="text-wrap py-2 leading-snug">
          {params.data.accountingSync.statusMessage}
        </div>
      );
    };

    const renderWaitingOn = (params: ICellRendererParams): string => {
      if (!params.data.accountingSync.waitingOn) return "";

      const waitingOnSplitted = params.data.accountingSync.waitingOn.split("/");
      const collection: DocType = waitingOnSplitted[2];
      const id = waitingOnSplitted[3];

      switch (collection) {
        case DocType.CUSTOMERS:
          const customer = waitingOnCustomerList.find(
            (customer) => customer.id === id,
          );
          if (customer) {
            return `Customer - ${customer.name}`;
          } else {
            return "";
          }
        case DocType.PRICEBOOKITEMS:
          const pbItem = waitingOnPbItemList.find((pbItem) => pbItem.id === id);
          if (pbItem) {
            return `Item - ${pbItem.title}`;
          } else {
            return "";
          }
        case DocType.INVOICES:
          const invoice = paymentsInvoices.find((invoice) => invoice.id === id);
          if (invoice) {
            return `Invoice - ${invoice.invoiceNumber}`;
          } else {
            return "";
          }
        case DocType.PAYMENTS:
          return "";
        default:
          const _exhaustivenessCheck: never = collection;
          return _exhaustivenessCheck;
      }
    };

    function handleClickWaitingOnEvent(data: any) {
      if (!data.accountingSync || !data.accountingSync.waitingOn)
        return undefined;

      const waitingOnSplitted = data.accountingSync.waitingOn.split("/");
      const collection: DocType = waitingOnSplitted[2];
      const id = waitingOnSplitted[3];

      switch (collection) {
        case DocType.CUSTOMERS:
          return goToCustomerPage(id);
        case DocType.PRICEBOOKITEMS:
          const pbItem = waitingOnPbItemList.find((pbItem) => pbItem.id === id);
          if (pbItem) {
            return onEditPBItem(pbItem);
          } else {
            return undefined;
          }
        case DocType.INVOICES:
          return openInvoiceDialog(id);
        case DocType.PAYMENTS:
          return undefined;
        default:
          const _exhaustivenessCheck: never = collection;
          return _exhaustivenessCheck;
      }
    }

    const renderTimestampLastSynced = (params: ICellRendererParams): string => {
      if (params.data.accountingSync.timestampLastSynced) {
        return convertToReadableTimestamp(
          params.data.accountingSync.timestampLastSynced,
        );
      }
      return "";
    };

    const renderTimestampLastSyncAttempt = (
      params: ICellRendererParams,
    ): string => {
      if (params.data.accountingSync.timestampLastSyncAttempt) {
        return convertToReadableTimestamp(
          params.data.accountingSync.timestampLastSyncAttempt,
        );
      }
      return "";
    };

    const renderTimestampNextSyncAttempt = (
      params: ICellRendererParams,
    ): string => {
      if (params.data.accountingSync.timestampNextSyncAttempt) {
        return convertToReadableTimestamp(
          params.data.accountingSync.timestampNextSyncAttempt,
        );
      }
      return "";
    };

    function handleClickEvent(data: any) {
      const docType: DocType = data.docType;
      switch (docType) {
        case DocType.CUSTOMERS:
          return goToCustomerPage(data.id);
        case DocType.PRICEBOOKITEMS:
          return onEditPBItem(data);
        case DocType.INVOICES:
          return openInvoiceDialog(data.id);
        case DocType.PAYMENTS:
          return openInvoiceDialog(data.invoiceID);
        default:
          const _exhaustivenessCheck: never = docType;
          return _exhaustivenessCheck;
      }
    }

    const columnDefs: ColDef[] = [
      {
        sortable: false,
        headerName: "Name",
        cellRenderer: renderName,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        onCellClicked: (ev) => handleClickEvent(ev.data),
        getQuickFilterText: (params: GetQuickFilterTextParams) => {
          let name;
          const docType: DocType = params.data.docType;
          switch (docType) {
            case DocType.CUSTOMERS:
              name = params.data.name;
              break;
            case DocType.PRICEBOOKITEMS:
              name = params.data.title;
              break;
            case DocType.INVOICES:
              name = params.data.invoiceNumber;
              break;
            case DocType.PAYMENTS:
              const invoice = paymentsInvoices.find(
                (invoice) => invoice.id === params.data.invoiceID,
              );
              if (invoice) {
                name = invoice.invoiceNumber;
              }
              break;
            default:
              const _exhaustivenessCheck: never = docType;
              return _exhaustivenessCheck;
          }

          return name;
        },
      },
      {
        field: "docType",
        headerName: "Type",
        width: 100,
        cellRenderer: renderType,
        filter: QuickbookDocTypeCustomFilter,
        tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
        onCellClicked: (ev) => handleClickEvent(ev.data),
      },
      // {
      //   field: "id",
      //   headerName: "Doc ID",
      //   width: 100,
      //   // cellRenderer: renderType,
      //   // filter: QuickbookDocTypeCustomFilter,
      //   // tooltipValueGetter: (params) => params.valueFormatted ?? params.value,
      //   // onCellClicked: (ev) => handleClickEvent(ev.data),
      // },
      {
        field: "accountingSync.syncStatus",
        headerName: "Sync Status",
        filter: AccountingSyncStatusCustomFilter,
        cellRenderer: renderSyncStatus,
        onCellClicked: (ev) => handleClickEvent(ev.data),
      },
      {
        field: "accountingSync.statusMessage",
        headerName: "Message",
        cellRenderer: renderMessage,
        autoHeight: true,
        minWidth: 240,
        flex: 2,
      },
      {
        field: "accountingSync.timestampLastSynced",
        headerName: "Last Synced",
        cellRenderer: renderTimestampLastSynced,
        onCellClicked: (ev) => handleClickEvent(ev.data),
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          getTimestampQuickFilterString(
            params.data?.accountingSync?.timestampLastSynced,
          ),
      },
      {
        field: "accountingSync.timestampLastSyncAttempt",
        headerName: "Last Sync Attempt",
        cellRenderer: renderTimestampLastSyncAttempt,
        onCellClicked: (ev) => handleClickEvent(ev.data),
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          getTimestampQuickFilterString(
            params.data?.accountingSync?.timestampLastSyncAttempt,
          ),
      },
      {
        field: "accountingSync.timestampNextSyncAttempt",
        headerName: "Next Sync Attempt",
        cellRenderer: renderTimestampNextSyncAttempt,
        onCellClicked: (ev) => handleClickEvent(ev.data),
        getQuickFilterText: (params: GetQuickFilterTextParams) =>
          getTimestampQuickFilterString(
            params.data?.accountingSync?.timestampNextSyncAttempt,
          ),
      },
      {
        field: "accountingSync.waitingOn",
        headerName: "Waiting On",
        cellRenderer: renderWaitingOn,
        onCellClicked: (ev) => handleClickWaitingOnEvent(ev.data),
      },
    ];

    return (
      <AgGridReact
        reactiveCustomComponents
        ref={gridRef}
        onGridReady={onGridReady}
        defaultColDef={commonColProps}
        className="mt-5 shadow"
        rowData={tableData}
        animateRows={true}
        rowSelection="multiple"
        enableCellTextSelection={true}
        suppressRowClickSelection={true}
        rowHeight={50}
        cacheQuickFilter={true}
        columnDefs={columnDefs}
        onSelectionChanged={(event) => {
          rowSelectionChanged(event);
        }}
      />
    );
  },
  (previous, next) => {
    const isTableDataTheSame = isEqual(previous.tableData, next.tableData);
    const isQuickbooksCustomersTheSame = isEqual(
      previous.quickbooksCustomers,
      next.quickbooksCustomers,
    );
    const isPaymentsInvoicesTheSame = isEqual(
      previous.paymentsInvoices,
      next.paymentsInvoices,
    );
    const isCustomerListTheSame = isEqual(
      previous.waitingOnCustomerList,
      next.waitingOnCustomerList,
    );
    const isPBItemListTheSame = isEqual(
      previous.waitingOnPbItemList,
      next.waitingOnPbItemList,
    );

    return (
      isTableDataTheSame &&
      isQuickbooksCustomersTheSame &&
      isPaymentsInvoicesTheSame &&
      isCustomerListTheSame &&
      isPBItemListTheSame
    );
  },
);

function getTimestampQuickFilterString(timestamp: unknown): string {
  if (timestamp != null) {
    return convertToReadableTimestamp(timestamp);
  } else {
    return "";
  }
}
